import React, { useState, useEffect } from 'react';

const DeviceCompatibilityStatus = ({ gameId }) => {
  const [storageStatus, setStorageStatus] = useState('checking');
  const [performanceLevel, setPerformanceLevel] = useState('unknown');
  const [storageUsage, setStorageUsage] = useState(0);

  useEffect(() => {
    const checkDeviceStatus = () => {
      try {
        // Check localStorage availability and usage
        const testKey = `storage_test_${Date.now()}`;
        localStorage.setItem(testKey, 'test');
        localStorage.removeItem(testKey);
        
        // Calculate storage usage
        let totalSize = 0;
        const keys = Object.keys(localStorage);
        for (const key of keys) {
          totalSize += localStorage.getItem(key)?.length || 0;
        }
        const usageMB = totalSize / (1024 * 1024);
        setStorageUsage(usageMB);
        
        if (usageMB > 4) {
          setStorageStatus('warning');
        } else if (usageMB > 2) {
          setStorageStatus('caution');
        } else {
          setStorageStatus('good');
        }

        // Check device performance
        let level = 'normal';
        const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        if (mobile) {
          level = 'mobile';
        } else if ('memory' in performance) {
          const memory = performance.memory;
          const usedMB = memory.usedJSHeapSize / (1024 * 1024);
          const totalMB = memory.totalJSHeapSize / (1024 * 1024);
          
          if (usedMB / totalMB > 0.8) {
            level = 'low';
          }
        }
        
        setPerformanceLevel(level);
        
      } catch (error) {
        setStorageStatus('error');
        setPerformanceLevel('unknown');
      }
    };

    checkDeviceStatus();
    
    // Re-check every 30 seconds
    const interval = setInterval(checkDeviceStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'good': return 'text-green-500';
      case 'caution': return 'text-yellow-500';
      case 'warning': return 'text-orange-500';
      case 'error': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'good': return '✅';
      case 'caution': return '⚠️';
      case 'warning': return '🚨';
      case 'error': return '❌';
      default: return '❓';
    }
  };

  const getPerformanceText = (level) => {
    switch (level) {
      case 'normal': return 'Normal Performance';
      case 'mobile': return 'Mobile Device (Conservative)';
      case 'low': return 'Low Memory (Reduced Saves)';
      default: return 'Unknown Performance';
    }
  };

  if (storageStatus === 'checking') {
    return (
      <div className="text-xs text-gray-500">
        🔍 Checking device compatibility...
      </div>
    );
  }

  return (
    <div className="text-xs space-y-1">
      <div className={`flex items-center gap-1 ${getStatusColor(storageStatus)}`}>
        <span>{getStatusIcon(storageStatus)}</span>
        <span>Storage: {storageUsage.toFixed(2)}MB</span>
      </div>
      
      <div className="text-gray-500">
        📱 {getPerformanceText(performanceLevel)}
      </div>
      
      {storageStatus === 'warning' && (
        <div className="text-orange-600 text-xs">
          ⚠️ Storage usage high - saves may be limited
        </div>
      )}
      
      {storageStatus === 'error' && (
        <div className="text-red-600 text-xs">
          ❌ Storage unavailable - auto-save disabled
        </div>
      )}
    </div>
  );
};

export default DeviceCompatibilityStatus;
